'use client';

import { m, useReducedMotion } from 'motion/react';
import type { CSSProperties, JSX } from 'react';

import { MOTION_POSITION_SMALL } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';

const DEFAULT_INACTIVE_LENGTH = '0.375rem' as const; // 6 px round dot
const DEFAULT_ACTIVE_LENGTH = '1.5rem' as const; // 24 px pill
const DEFAULT_THICKNESS = '0.375rem' as const; // 6 px

const REDUCED_MOTION_TRANSITION: { readonly duration: 0 } = { duration: 0 } as const;

export type CarouselDotsOrientation = 'horizontal' | 'vertical';

export interface CarouselDotsProps {
  /** Total number of dots. The strip auto-hides when count <= 1. */
  count: number;
  /** Index of the active slide (0-based). */
  activeIndex: number;
  /** Strip orientation. Default 'horizontal'. */
  orientation?: CarouselDotsOrientation;

  /** Click handler. When provided, dots render as buttons (per-slide navigation). When omitted, dots are decorative spans. */
  onSelect?: (index: number) => void;
  /** Per-dot aria-label generator. Required when `onSelect` is provided. */
  itemAriaLabel?: (index: number, total: number) => string;
  /** Wrapper aria-label, e.g. carousel name. */
  ariaLabel?: string;

  /**
   * Auto-progress fill duration in ms. When set, the active dot animates a
   * linear progress fill (`scaleX` / `scaleY` 0→1) tied to this duration —
   * matching the market-hero / intro-widget treatment.
   * When omitted, no progress animation; the active dot is just wider.
   */
  progressDurationMs?: number;
  /** Reset key for the progress animation; defaults to `activeIndex`. Re-mounts the fill so it restarts from 0 on every slide change. */
  progressResetKey?: string | number;

  /** Length of the active dot pill along the strip axis. Default '1.5rem'. */
  activeLength?: string;
  /** Length of inactive dots along the strip axis. Default '0.375rem'. */
  inactiveLength?: string;
  /** Cross-axis dimension (height for horizontal, width for vertical). Default '0.375rem'. */
  thickness?: string;

  /** Tailwind class for the active dot's track background. Default subtle 'bg-foreground-tertiary/25'. */
  activeTrackClass?: string;
  /** Tailwind class for inactive dots. Default 'bg-foreground-tertiary/50'. Hover state added automatically when interactive. */
  inactiveClass?: string;
  /** Tailwind class for the progress fill. Default 'bg-foreground-tertiary/50'. */
  progressFillClass?: string;

  className?: string;
  /** Wrapper test-id. */
  testId?: string;
  /** Per-dot test-id generator. */
  dotTestId?: (index: number) => string;
}

/**
 * Canonical pagination-dots primitive used across every carousel surface in
 * the app — featured market hero, intro widget, theses preview, and any future
 * carousel. Mirrors the market-hero treatment by default: small round inactive
 * dots, an active dot that widens into a pill, and an optional linear
 * progress fill animation tied to an auto-advance window.
 *
 * Variants are expressed via props rather than discriminated `variant`s:
 *  - `onSelect` set → clickable navigation. Omitted → decorative.
 *  - `progressDurationMs` set → active dot has a progress-fill animation.
 *    Omitted → just the widening transition, no fill.
 *  - `orientation: 'vertical'` → strip flows top-to-bottom; the active dot
 *    grows vertically; the progress fill animates `scaleY`.
 */
export function CarouselDots(props: CarouselDotsProps): JSX.Element | null {
  const {
    count,
    activeIndex,
    orientation = 'horizontal',
    onSelect,
    itemAriaLabel,
    ariaLabel,
    progressDurationMs,
    progressResetKey = activeIndex,
    activeLength = DEFAULT_ACTIVE_LENGTH,
    inactiveLength = DEFAULT_INACTIVE_LENGTH,
    thickness = DEFAULT_THICKNESS,
    activeTrackClass = 'bg-foreground-tertiary/25',
    inactiveClass = 'bg-foreground-tertiary/50',
    progressFillClass = 'bg-foreground-tertiary/50',
    className,
    testId,
    dotTestId,
  } = props;

  const reduceMotion = useReducedMotion();
  const reduce = reduceMotion === true;
  const isVertical = orientation === 'vertical';
  const interactive = onSelect !== undefined;

  if (count <= 1) return null;

  const progressAnimClass = isVertical ? 'morfi-carousel-progress-y' : 'morfi-carousel-progress-x';

  return (
    <ul
      className={cn('flex items-center gap-1.5', isVertical && 'flex-col', className)}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {Array.from({ length: count }, (_, i) => {
        const isActive = i === activeIndex;
        const dotAnimate: { width?: string; height?: string } = isVertical
          ? { height: isActive ? activeLength : inactiveLength }
          : { width: isActive ? activeLength : inactiveLength };
        const dotStyle: { width: string } | { height: string } = isVertical
          ? { width: thickness }
          : { height: thickness };
        const dotClass = cn(
          'block shrink-0 overflow-hidden rounded-full',
          isActive ? activeTrackClass : inactiveClass,
          interactive && !isActive && 'hover:bg-foreground-tertiary/70',
          interactive &&
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary',
        );

        // The active dot's auto-advance fill. Driven by a CSS keyframe
        // animation (`globals.css`), not motion: motion's `m.*` mount
        // animation does NOT replay on a key-driven remount inside this
        // LazyMotion subtree (verified — the fill stayed at `scaleX(1)` across
        // every slide change), so the bar never visibly filled. The keyed
        // remount restarts the CSS animation from scaleX(0) on each slide;
        // reduced-motion is handled in CSS (the animation is dropped, leaving
        // the fill at rest = full).
        const progressFill =
          isActive && progressDurationMs !== undefined ? (
            <span
              key={progressResetKey}
              aria-hidden="true"
              style={
                {
                  '--carousel-progress-duration': `${progressDurationMs}ms`,
                } as CSSProperties
              }
              className={cn('block h-full w-full', progressAnimClass, progressFillClass)}
            />
          ) : null;

        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: REASON: dots are generated from a count (`Array.from({ length: count })`), there is no stable id — index IS the identity.
          <li key={i}>
            {interactive ? (
              // ALLOW-RAW-BUTTON: tiny pagination dot — AnimatedButton would dominate the row visually
              <m.button
                type="button"
                onClick={(): void => onSelect(i)}
                aria-label={itemAriaLabel?.(i, count)}
                aria-current={isActive ? 'true' : undefined}
                initial={false}
                animate={dotAnimate}
                transition={reduce ? REDUCED_MOTION_TRANSITION : MOTION_POSITION_SMALL}
                className={dotClass}
                style={dotStyle}
                data-active={isActive ? 'true' : undefined}
                data-testid={dotTestId?.(i)}
              >
                {progressFill}
              </m.button>
            ) : (
              <m.span
                aria-hidden="true"
                initial={false}
                animate={dotAnimate}
                transition={reduce ? REDUCED_MOTION_TRANSITION : MOTION_POSITION_SMALL}
                className={dotClass}
                style={dotStyle}
                data-active={isActive ? 'true' : undefined}
                data-testid={dotTestId?.(i)}
              >
                {progressFill}
              </m.span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
